import { GlobalCacheStore } from '@inter-ikea-kompis/utilities';
import CacheStoreType from '../../enums/CacheStoreType.js';
import ChinaAuthenticationService from '../../services/authentication/china/ChinaAuthenticationService.js';
import IowsAuthenticationService from '../../services/authentication/iows/IowsAuthenticationService.js';
import MvEcomAuthenticationService from '../../services/authentication/mv-ecom/MvEcomAuthenticationService.js';
import NifAuthenticationService from '../../services/authentication/nif/NifAuthenticationService.js';
import OneWebAuthenticationService from '../../services/authentication/one-web/OneWebAuthenticationService.js';
/**
 * This class is used for storing items in a queue.
 */
export default class AuthenticationQueue {
    /**
     * Constructor.
     *
     * @param cache CacheStore.
     */
    constructor(cache) {
        this.cache = cache || new GlobalCacheStore();
    }
    /**
     * Creates user authentication.
     *
     * @param options Options.
     * @param options.consumer Consumer.
     * @param options.contract Contract.
     * @param options.endpoint IOWS authentication endpoint.
     * @returns Promise object.
     */
    async getAuthenticationIows(options) {
        const cacheKey = 'iowsAuthenticationQueue';
        const cacheStore = this.cache.getStore(CacheStoreType.authentication);
        const queuedPromises = cacheStore.get(cacheKey);
        if (queuedPromises) {
            return new Promise((resolve, reject) => queuedPromises.push({ resolve, reject }));
        }
        const promiseQueue = [];
        const authService = new IowsAuthenticationService(options);
        let authentication;
        cacheStore.set(cacheKey, promiseQueue);
        try {
            authentication = await authService.getUser();
            for (const promise of promiseQueue) {
                promise.resolve(authentication);
            }
        }
        catch (error) {
            for (const promise of promiseQueue) {
                promise.reject(error);
            }
            cacheStore.delete(cacheKey);
            throw error;
        }
        cacheStore.delete(cacheKey);
        return authentication;
    }
    /**
     * Gets or creates user token.
     *
     * @param options Options.
     * @param options.endpoint OneWeb authentication endpoint.
     * @returns Promise object.
     */
    async getAuthTokenOneWeb(options) {
        const cacheKey = 'oneWebAuthenticationQueue';
        const cacheStore = this.cache.getStore(CacheStoreType.authentication);
        const queuedPromises = cacheStore.get(cacheKey);
        if (queuedPromises) {
            return new Promise((resolve, reject) => queuedPromises.push({ resolve, reject }));
        }
        const promiseQueue = [];
        const authService = new OneWebAuthenticationService(options);
        let authentication = undefined;
        cacheStore.set(cacheKey, promiseQueue);
        try {
            authentication = await authService.getToken();
            for (const promise of promiseQueue) {
                promise.resolve(authentication);
            }
        }
        catch (error) {
            for (const promise of promiseQueue) {
                promise.reject(error);
            }
            cacheStore.delete(cacheKey);
            throw error;
        }
        cacheStore.delete(cacheKey);
        return authentication;
    }
    /**
     * Fetches user token from MvEcom.
     *
     * @param options Options.
     * @param options.endpoint MvEcom authentication endpoint.
     * @param options.locale Current locale.
     * @returns Promise object.
     */
    async getAuthTokenMvEcom(options) {
        const cacheKey = 'mvEcomAuthenticationQueue';
        const cacheStore = this.cache.getStore(CacheStoreType.authentication);
        const queuedPromises = cacheStore.get(cacheKey);
        if (queuedPromises) {
            return new Promise((resolve, reject) => queuedPromises.push({ resolve, reject }));
        }
        const promiseQueue = [];
        const authService = new MvEcomAuthenticationService(options);
        let authentication = null;
        cacheStore.set(cacheKey, promiseQueue);
        try {
            authentication = await authService.getToken();
            for (const promise of promiseQueue) {
                promise.resolve(authentication);
            }
        }
        catch (error) {
            for (const promise of promiseQueue) {
                promise.reject(error);
            }
            cacheStore.delete(cacheKey);
            throw error;
        }
        cacheStore.delete(cacheKey);
        return authentication;
    }
    /**
     * Gets or creates user token.
     *
     * @param options Options.
     * @param options.refreshTokenEndpoint Refresh token endpoint.
     * @param options.loginPageEndpoint Login endpoint.
     * @param options.login Force login. If omitted try fetch existing token.
     * @returns Promise object.
     */
    async getAuthTokenChina(options) {
        const cacheKey = 'chinaAuthenticationQueue';
        const cacheStore = this.cache.getStore(CacheStoreType.authentication);
        const queuedPromises = cacheStore.get(cacheKey);
        if (queuedPromises) {
            return new Promise((resolve, reject) => queuedPromises.push({ resolve, reject }));
        }
        const promiseQueue = [];
        const authService = new ChinaAuthenticationService({
            refreshTokenEndpoint: options.refreshTokenEndpoint,
            loginPageEndpoint: options.loginPageEndpoint
        });
        let authentication;
        cacheStore.set(cacheKey, promiseQueue);
        try {
            authentication = await (options.login ? authService.login() : authService.getToken());
            for (const promise of promiseQueue) {
                promise.resolve(authentication);
            }
        }
        catch (error) {
            for (const promise of promiseQueue) {
                promise.reject(error);
            }
            cacheStore.delete(cacheKey);
            throw error;
        }
        cacheStore.delete(cacheKey);
        return authentication;
    }
    /**
     * Creates user authentication.
     *
     * @param options Options.
     * @param options.endpoint Nif authentication endpoint.
     * @returns Promise object.
     */
    async authenticateNif(options) {
        const cacheKey = 'nifAuthenticationQueue';
        const cacheStore = this.cache.getStore(CacheStoreType.authentication);
        const queuedPromises = cacheStore.get(cacheKey);
        if (queuedPromises) {
            return new Promise((resolve, reject) => queuedPromises.push({ resolve, reject }));
        }
        const promiseQueue = [];
        const authService = new NifAuthenticationService(options);
        let authentication;
        cacheStore.set(cacheKey, promiseQueue);
        try {
            authentication = await authService.authenticate();
            for (const promise of promiseQueue) {
                promise.resolve(authentication);
            }
        }
        catch (error) {
            for (const promise of promiseQueue) {
                promise.reject(error);
            }
            cacheStore.delete(cacheKey);
            throw error;
        }
        cacheStore.delete(cacheKey);
        return authentication;
    }
}
//# sourceMappingURL=AuthenticationQueue.js.map